import { redirect } from "@tanstack/react-router";
import { HTTPError, TimeoutError } from "ky";
import { fetchAuthUser, hydrateAuthUser } from "@/lib/auth-api";
import { useAuthStore } from "@/stores/auth";

function isTransientAuthReadError(error: unknown): boolean {
  if (error instanceof HTTPError) return [500, 502, 503, 504].includes(error.response.status);
  return error instanceof TypeError || error instanceof TimeoutError;
}

async function fetchAuthenticatedUserWithRetry() {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetchAuthUser();
    } catch (error) {
      if (!isTransientAuthReadError(error) || attempt >= 6) throw error;
      // Dev API restarts and short gateway outages should keep the route
      // pending, not briefly render an unauthenticated or read-only workspace.
      await new Promise((resolve) => setTimeout(resolve, Math.min(250 * 2 ** attempt, 1_500)));
    }
  }
}

export async function loadAuthenticatedUser(): Promise<void> {
  // beforeLoad runs on every navigation; only the first load lacks a verified user.
  const hydrated = useAuthStore.getState().user !== null;
  try {
    hydrateAuthUser(await (hydrated ? fetchAuthUser() : fetchAuthenticatedUserWithRetry()));
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 401) {
      throw redirect({ to: "/login" });
    }
    // Mid-session, the user verified earlier stays authoritative: a network blip on a
    // later navigation must not replace the whole app with an error screen.
    if (hydrated) return;
    // A failed auth read is not evidence of a lower role. Let the route show an
    // error instead of mounting privileged screens with an empty user store.
    throw error;
  }
}
